import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { inject } from '@angular/core';
import { QuoteService } from '@app/core/services/models/quote.service';
import { BaseUrlService } from '@app/core/services/base-url.service';
import { ToastService } from '@app/core/services/toast.service';
import { primengImports } from '@app/core/primeng';
import { ConsoleService } from '@app/core/services/console.service';

@Component({
  selector: 'app-quotes-preview',
  standalone: true,
  imports: [
    CommonModule,
    PdfViewerModule,
    ...primengImports
  ],
  templateUrl: './quotes-preview.component.html',
  styleUrl: './quotes-preview.component.scss'
})
export class QuotesPreviewComponent implements OnInit, OnDestroy {
  private baseUrl = inject(BaseUrlService);

  // Quote data
  record: any = null;
  invoiceRef: string | null = null;
  invoiceUrl: string | null = null;
  pdfUrlRaw: string | null = null;
  pdfBlobUrl: string | null = null;

  // Quote UUID
  quoteUuid: string | null = null;

  // Loading
  loading: boolean = false;
  processing: boolean = false;
  loadingPdf: boolean = false;

  // Status
  canAccept: boolean = true;

  get estAccepte(): boolean {
    return String(this.record?.status?.name ?? '').toLowerCase().includes('accept');
  }
  canDecline: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private quoteService: QuoteService,
    private toastService: ToastService,
    private http: HttpClient,
    private _consoleService: ConsoleService
  ) {}

  ngOnDestroy() {
    // Clean up blob URL to prevent memory leaks
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  ngOnInit() {
    // Configure PDF.js worker
    this.configurePdfWorker();
    
    this.quoteUuid = this.route.snapshot.paramMap.get('uuid');
    if (this.quoteUuid) {
      this.loadQuote();
    } else {
      this.toastService.show('Devis introuvable', 'error');
    }
  }

  // Configure PDF.js worker to use local worker file
  configurePdfWorker() {
    // Set worker source to local file
    const pdfjsVersion = '4.8.69';
    const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
    
    // Set global worker source for pdfjs
    if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    }
  }

  // Load quote
  loadQuote() {
    if (!this.quoteUuid) return;

    this.loading = true;

    // Use public method to fetch quote without authentication
    this.quoteService.getByUuid(this.quoteUuid).subscribe({
      next: (quote: any) => {
        this._consoleService.logVar('quote', quote);

        this.record = quote;
        
        this.invoiceRef = quote.invoice_ref;
        this.invoiceUrl = quote.pdf_url;

        // PDF généré à la demande (pdf_url renseigné par generate-pdf/{id}).
        // S'il est absent, on le génère puis on l'affiche ; sinon on charge l'existant.
        this.preparePdf(quote);

        // Statut lu par son nom : les ids diffèrent d'une instance à l'autre.
        const statut = String(quote?.status?.name ?? '').toLowerCase();
        const tranche = ['accept', 'refus', 'converti', 'annul', 'partiellement'].some(m => statut.includes(m));
        this.canAccept = !tranche;
        this.canDecline = !tranche;

        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading quote:', err);
        this.toastService.show('Erreur lors du chargement du devis', 'error');
        this.loading = false;
      }
    });
  }

  // Accept quote
  acceptQuote() {
    if (!this.quoteUuid || !this.canAccept) return;

    this.processing = true;

    this.quoteService.accept(this.quoteUuid).subscribe({
      next: () => {
        this.toastService.show('Devis accepté avec succès', 'success');
        this.canAccept = false;
        this.canDecline = false;
        this.processing = false;
        // Reload quote to update status
        setTimeout(() => this.loadQuote(), 1000);
      },
      error: (err) => {
        console.error('Error accepting quote:', err);
        this.toastService.show('Erreur lors de l\'acceptation du devis', 'error');
        this.processing = false;
      }
    });
  }

  // Decline quote
  rejectQuote() {
    if (!this.quoteUuid || !this.canDecline) return;

    this.processing = true;

    this.quoteService.reject(this.quoteUuid).subscribe({
      next: () => {
        this.toastService.show('Devis refusé', 'success');
        this.canAccept = false;
        this.canDecline = false;
        this.processing = false;
        // Reload quote to update status
        setTimeout(() => this.loadQuote(), 1000);
      },
      error: (err) => {
        console.error('Error declining quote:', err);
        this.toastService.show('Erreur lors du refus du devis', 'error');
        this.processing = false;
      }
    });
  }

  // Load PDF as blob to avoid CORS
  /**
   * Prépare l'URL du PDF puis le charge. Si le devis n'a pas encore de pdf_url
   * (PDF jamais généré), on déclenche la génération côté API et on affiche le résultat.
   * Repli sur un éventuel pdf_url existant si la génération échoue.
   */
  private preparePdf(quote: any): void {
    const show = (url: string | null | undefined): void => {
      if (!url) {
        this.toastService.show('PDF indisponible pour ce devis', 'error');
        return;
      }
      this.invoiceUrl = url;
      this.pdfUrlRaw = `${this.baseUrl.uploadPath}/pdf/${url}`;
      this.loadPdfAsBlob();
    };

    if (quote?.pdf_url) {
      show(quote.pdf_url);
      return;
    }
    // Page PUBLIQUE : le PDF est généré côté serveur par l'aperçu public
    // (getEntryForPreview). On n'appelle JAMAIS la route authentifiée generate-pdf
    // ici, sinon le client anonyme reçoit un 401 -> l'intercepteur le redirige vers
    // le formulaire de connexion. S'il manque encore, simple indisponibilité.
    show(null);
  }

  loadPdfAsBlob() {
    if (!this.pdfUrlRaw) return;
    this._consoleService.logVar('pdfUrlRaw', this.pdfUrlRaw);
    this.loadingPdf = true;

    // Clean up previous blob URL
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }

    // Download PDF as blob
    this.http.get(this.pdfUrlRaw, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Accept': 'application/pdf'
      })
    }).subscribe({
      next: (blob: Blob) => {
        // Create blob URL
        this.pdfBlobUrl = URL.createObjectURL(blob);
        this.loadingPdf = false;
      },
      error: (err) => {
        console.error('Error loading PDF:', err);
        this.toastService.show('Erreur lors du chargement du PDF', 'error');
        this.loadingPdf = false;
      }
    });
  }

  // Download PDF
  downloadPDF() {
    if (!this.pdfUrlRaw) return;
    
    // Open in new tab
    window.open(this.pdfUrlRaw, '_blank');
  }
}

